import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Plus, Trash2, X, Save, UserPlus } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function UsersManager() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'editor' });

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/auth/users`, { withCredentials: true });
      setUsers(res.data);
    } catch { setUsers([]); }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/api/auth/users`, form, { withCredentials: true });
      setForm({ name: '', email: '', password: '', role: 'editor' });
      setShowCreate(false);
      fetchUsers();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to create user'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await axios.delete(`${API}/api/auth/users/${id}`, { withCredentials: true });
      fetchUsers();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to delete'); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-white">User Management</h1>
        <button onClick={() => setShowCreate(true)} className="bg-[#F2B705] text-black font-bold text-xs uppercase px-5 py-2.5 hover:bg-[#D9A404] flex items-center gap-2">
          <UserPlus size={14} /> Add User
        </button>
      </div>

      {showCreate && (
        <div className="bg-[#161616] border border-[#F2B705]/20 p-6 mb-6 space-y-4">
          <h3 className="text-sm font-bold text-white">Create New User</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="text-xs uppercase text-[#888] font-bold mb-1 block">Name</label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-transparent border-white/10 text-white text-sm h-10 focus:border-[#F2B705]" /></div>
            <div><label className="text-xs uppercase text-[#888] font-bold mb-1 block">Email</label>
              <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} type="email" className="bg-transparent border-white/10 text-white text-sm h-10 focus:border-[#F2B705]" /></div>
            <div><label className="text-xs uppercase text-[#888] font-bold mb-1 block">Password</label>
              <Input value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} type="password" className="bg-transparent border-white/10 text-white text-sm h-10 focus:border-[#F2B705]" /></div>
            <div><label className="text-xs uppercase text-[#888] font-bold mb-1 block">Role</label>
              <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} className="w-full bg-[#0a0a0a] border border-white/10 text-white text-sm h-10 px-3">
                <option value="editor">Editor</option><option value="superadmin">Super Admin</option>
              </select></div>
          </div>
          <div className="flex gap-3">
            <button onClick={handleCreate} className="bg-[#F2B705] text-black font-bold text-xs uppercase px-5 py-2.5 hover:bg-[#D9A404] flex items-center gap-2">
              <Save size={14} /> Create User
            </button>
            <button onClick={() => setShowCreate(false)} className="border border-white/10 text-[#888] text-xs uppercase px-5 py-2.5 hover:text-white flex items-center gap-2">
              <X size={14} /> Cancel
            </button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {users.map(u => (
          <div key={u._id} className="bg-[#161616] border border-white/10 p-5 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-white">{u.name}</p>
              <p className="text-xs text-[#666]">{u.email} &middot; <span className="text-[#F2B705] uppercase">{u.role}</span></p>
            </div>
            {u._id !== currentUser?.id && (
              <button onClick={() => handleDelete(u._id)} className="text-[#888] hover:text-red-400 p-2"><Trash2 size={14} /></button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
